import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useCloudSecurity } from '../context/CloudSecurityContext';
import { getFrameworkMappingForCheck } from '../data/frameworkMappings';

const shineEffect = keyframes`
  0% { left: -100%; }
  100% { left: 100%; }
`;

const Container = styled.div`
  background: #0a0e27;
  padding: 15px;
  margin-bottom: 15px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateX(5px);
    background: #0f1329;
  }
  
  &:hover::after {
    animation: ${shineEffect} 0.5s;
  }
`;

const CheckboxWrapper = styled.div`
  position: relative;
`;

const StyledCheckbox = styled.input`
  width: 24px;
  height: 24px;
  cursor: pointer;
  appearance: none;
  background: #2d3561;
  border-radius: 6px;
  position: relative;
  transition: all 0.3s ease;
  
  &:checked {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
  
  &:checked::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 16px;
    font-weight: bold;
  }
`;

const Content = styled.div`
  flex: 1;
`;

const Title = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
  color: #e0e6ed;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Description = styled.div`
  font-size: 0.9em;
  color: #8892b0;
  line-height: 1.5;
`;

const RiskLevel = styled.span`
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.85em;
  font-weight: 600;
  text-transform: uppercase;
  
  background: ${props => {
    switch(props.risk) {
      case 'high':
        return 'rgba(244, 63, 94, 0.2)';
      case 'medium':
        return 'rgba(251, 146, 60, 0.2)';
      case 'low':
        return 'rgba(59, 130, 246, 0.2)';
      default:
        return 'rgba(102, 126, 234, 0.2)';
    }
  }};
  
  color: ${props => {
    switch(props.risk) {
      case 'high':
        return '#f43f5e';
      case 'medium':
        return '#fb923c';
      case 'low':
        return '#3b82f6';
      default:
        return '#667eea';
    }
  }};
  
  border: 1px solid ${props => {
    switch(props.risk) {
      case 'high':
        return 'rgba(244, 63, 94, 0.3)';
      case 'medium':
        return 'rgba(251, 146, 60, 0.3)';
      case 'low':
        return 'rgba(59, 130, 246, 0.3)';
      default:
        return 'rgba(102, 126, 234, 0.3)';
    }
  }};
`;

const FrameworkMappings = styled.div`
  margin-top: 8px;
  font-size: 0.85em;
  color: #667eea;
  border-top: 1px solid rgba(102, 126, 234, 0.2);
  padding-top: 8px;
  display: ${props => props.expanded ? 'block' : 'none'};
`;

const FrameworkBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  margin: 2px;
  font-size: 0.85em;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border: 1px solid rgba(102, 126, 234, 0.2);
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: #667eea;
  padding: 0;
  font-size: 0.85em;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-left: 10px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const InfoIcon = styled.span`
  margin-left: 5px;
  font-size: 0.9em;
`;

const CheckItem = ({ check, catIndex, checkIndex }) => {
  const { assessmentResults, toggleCheck } = useCloudSecurity();
  const [expanded, setExpanded] = useState(false);
  
  const isChecked = 
    assessmentResults[catIndex] && 
    assessmentResults[catIndex][checkIndex] === true;
  
  const handleToggle = (e) => {
    e.stopPropagation();
    toggleCheck(catIndex, checkIndex, !isChecked);
  };
  
  const handleClick = () => {
    toggleCheck(catIndex, checkIndex, !isChecked);
  };
  
  const toggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };
  
  // Get framework mappings for this check
  const mappings = getFrameworkMappingForCheck(check.title);
  const hasMappings = Object.keys(mappings).length > 0;
  
  return (
    <Container onClick={handleClick}>
      <CheckboxWrapper>
        <StyledCheckbox 
          type="checkbox" 
          checked={isChecked} 
          onChange={handleToggle} 
        />
      </CheckboxWrapper>
      
      <Content>
        <Title>
          <div>{check.title}</div>
          {hasMappings && (
            <ExpandButton onClick={toggleExpand}>
              {expanded ? 'Hide mappings' : 'Show mappings'}
              <InfoIcon>{expanded ? '▲' : '▼'}</InfoIcon>
            </ExpandButton>
          )}
        </Title>
        <Description>{check.description}</Description>
        
        {hasMappings && (
          <FrameworkMappings expanded={expanded}>
            {Object.entries(mappings).map(([framework, controls]) => (
              <div key={framework} style={{ margin: '5px 0' }}>
                <strong>{framework}:</strong>{' '}
                <FrameworkBadge>{controls}</FrameworkBadge>
              </div>
            ))}
          </FrameworkMappings>
        )}
      </Content>
      
      <RiskLevel risk={check.risk}>
        {check.risk} risk
      </RiskLevel>
    </Container>
  );
};

export default CheckItem; 